import { useEffect } from 'react';
import { X, Building2 } from 'lucide-react';
import type { SampleCompany } from '../lib/sampleCompanies';

interface OrganizationDetailsModalProps {
  open: boolean;
  onClose: () => void;
  /** Full demo profile when the selection matches `SAMPLE_COMPANIES`. */
  profile: SampleCompany | null;
  /** Shown when only name/EIN are known (e.g. typed in settings). */
  fallbackName: string;
  fallbackEin: string;
}

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  if (value == null || String(value).trim() === '') return null;
  return (
    <div className="border-b border-slate-100 py-2.5 last:border-0">
      <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-900 whitespace-pre-wrap">{value}</dd>
    </div>
  );
}

export function OrganizationDetailsModal({
  open,
  onClose,
  profile,
  fallbackName,
  fallbackEin,
}: OrganizationDetailsModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex max-h-[min(90vh,36rem)] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="org-details-title"
      >
        <div className="flex flex-shrink-0 items-start justify-between gap-3 border-b border-slate-200 p-5">
          <div className="min-w-0">
            <h2
              id="org-details-title"
              className="flex items-center gap-2 text-lg font-semibold text-slate-900"
            >
              <Building2 className="h-5 w-5 shrink-0 text-slate-600" />
              <span className="truncate">Organization details</span>
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Demo profile for proofreading—replace with live data when available.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-2">
          {profile ? (
            <dl>
              <Row label="Legal name" value={profile.name} />
              <Row label="EIN" value={profile.ein} />
              <Row label="DBA" value={profile.dba} />
              <Row
                label="Address"
                value={`${profile.addressLine1}\n${profile.city}, ${profile.state} ${profile.zip}`}
              />
              <Row label="Phone" value={profile.phone} />
              <Row label="Email" value={profile.email} />
              <Row label="Website" value={profile.website} />
              <Row label="Fiscal year end" value={profile.fiscalYearEnd} />
              <Row label="Incorporation date" value={profile.incorporationDate} />
              <Row label="IRS determination (sample)" value={profile.irsDeterminationLetter} />
              <Row label="Primary contact" value={`${profile.contactName}, ${profile.contactTitle}`} />
              <Row label="Mission" value={profile.mission} />
              <Row label="Internal notes" value={profile.notes} />
            </dl>
          ) : (
            <div className="py-4 text-sm text-slate-700">
              <p className="font-medium text-slate-900">Limited context</p>
              <p className="mt-2 text-slate-600">
                No full demo profile is on file for this selection. Below is what will still be sent to
                the assistant:
              </p>
              <dl className="mt-4 space-y-3">
                <Row label="Name" value={fallbackName.trim() || '—'} />
                <Row label="EIN" value={fallbackEin.trim() || '—'} />
              </dl>
            </div>
          )}
        </div>

        <div className="flex flex-shrink-0 justify-end border-t border-slate-200 p-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
